import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { appState } from '@/app/appState';
import { chatState } from '@/app/chatState';
import { ActionHandler, useModalClose, useRefState } from './UI/tools';
import { Remove, Save } from './UI/svgs';
import { RealtimeClient, type RecognitionResult } from '@speechmatics/real-time-client';
import { set } from 'mobx';
import { Request } from '@/components/CRUD';
import { updateTagSpeakerProfile, getTranscription, clearTranscription, generateEventTranscriptionSummary, postFrontendError } from '@/api/sdk.gen';
import { UpdateTagSpeakerProfileData, GenerateEventTranscriptionSummaryData } from '@/api/types.gen';
import { SpeakerProfileRecorder } from './SpeakerProfileRecorder';
import { hasProfileForDevice, getAllIdentifiers, appendProfile } from '../utils/speakerProfileSerializer';
import { scrollIntoContainer } from '@/components/scrollUtils';
// Speechmatics WebSocket URL
const SM_URL = "wss://eu.rt.speechmatics.com/v2";

// Same-speaker pauses below this are merged silently into one segment. Must equal
// SEGMENT_MERGE_MAX_SECONDS in backend/src/utils/transcription_preprocessor.py, or event citation
// line numbers drift (see getSegmentLineRanges).
const SEGMENT_MERGE_MAX_SECONDS = 0.8;

// Same-speaker pauses below this stay in one turn with an inline marker; longer ones start a new
// turn header. Must equal INLINE_BREAK_MAX_SECONDS in backend/src/utils/transcription_preprocessor.py.
const INLINE_BREAK_MAX_SECONDS = 10;

interface TranscriptionComponentProps {
    onTranscriptionUpdate?: (text: string) => void;
    eventId?: number;
    // 1-based line number (in the backend wrapped numbering) that the component should scroll
    // to and briefly highlight once the transcription has loaded. Used by event-citation clicks
    // to jump straight to the cited passage.
    scrollToLine?: number;
    // 1-based end line of the cited range. Together with `scrollToLine` this highlights every
    // segment the citation spans (not just the first). Defaults to `scrollToLine` when omitted.
    scrollToEndLine?: number;
    // Increments on each citation click so the same line range can re-flash without remounting.
    scrollNonce?: number;
}

interface Segment {
    speaker: string;
    text: string;
    partial?: string;
    startTime?: number;
    endTime?: number;
    duration?: number;
    pauseAfterPrev?: number;
    speakerChanged?: boolean;
}

interface SpeakerRun {
    speaker: string;
    text: string;
    startTime: number;
    endTime: number;
}

// Join Speechmatics results into text, honouring `attaches_to` (punctuation attaches to the previous word by default).
function joinResults(results: RecognitionResult[]): string {
    let out = '';
    let glueNext = false;
    for (const r of results) {
        const content = r.alternatives?.[0]?.content;
        if (!content) continue;
        const attachesPrev = r.attaches_to === 'previous' || r.attaches_to === 'both'
            || (r.type === 'punctuation' && !r.attaches_to);
        if (out && !attachesPrev && !glueNext) out += ' ';
        out += content;
        glueNext = r.attaches_to === 'next' || r.attaches_to === 'both';
    }
    return out;
}

// Split a final transcript into consecutive same-speaker runs timed by their words.
// Message metadata start/end times describe the whole (contiguous) audio window including silence,
// so they cannot be used for pause or phrase-duration measurements.
function splitIntoSpeakerRuns(results: RecognitionResult[]): SpeakerRun[] {
    const groups: { speaker: string; items: RecognitionResult[]; start?: number; end?: number }[] = [];
    let current: (typeof groups)[number] | null = null;
    for (const r of results) {
        if (r.type === 'punctuation') {
            if (!current) {
                current = { speaker: 'Unknown', items: [] };
                groups.push(current);
            }
            current.items.push(r);
            continue;
        }
        const speaker = r.alternatives?.[0]?.speaker || 'Unknown';
        if (!current || (current.start !== undefined && current.speaker !== speaker)) {
            current = { speaker, items: [] };
            groups.push(current);
        }
        if (current.start === undefined) {
            current.speaker = speaker;
            current.start = r.start_time;
        }
        current.end = r.end_time;
        current.items.push(r);
    }
    return groups
        .filter(g => g.start !== undefined && g.end !== undefined)
        .map(g => ({ speaker: g.speaker, text: joinResults(g.items), startTime: g.start!, endTime: g.end! }))
        .filter(run => run.text);
}

// Serialize segments into a storable string format: [Speaker|pause|duration] text
function serializeSegments(segments: Segment[]): string {
    return segments
        .filter(s => s.text)
        .map(s => {
            const speaker = s.speaker || 'Unknown';
            const pause = (s.pauseAfterPrev ?? 0).toFixed(2);
            const dur = (s.duration ?? 0).toFixed(2);
            return `[${speaker}|${pause}s|${dur}s] ${s.text}`;
        })
        .join('\n');
}

// Deserialize stored string back into Segment array
function deserializeSegments(text: string): Segment[] {
    if (!text || !text.trim()) return [];
    const lines = text.split('\n').filter(l => l.trim());
    const segmentRegex = /^\[([^|]*)\|([0-9.]+)s(?:\|([0-9.]+)s)?\] (.*)$/;
    const raw: Segment[] = [];
    let hasStructuredFormat = false;

    for (const line of lines) {
        const match = line.match(segmentRegex);
        if (match) {
            hasStructuredFormat = true;
            raw.push({
                speaker: match[1] || 'Unknown',
                text: match[4],
                pauseAfterPrev: parseFloat(match[2]),
                duration: match[3] ? parseFloat(match[3]) : undefined,
            });
        } else if (hasStructuredFormat && raw.length > 0) {
            raw[raw.length - 1].text += ' ' + line.trim();
        }
    }

    // Fallback: treat entire text as a single plain segment
    if (!hasStructuredFormat) {
        return [{ speaker: 'Unknown', text: text.trim() }];
    }

    // Merge consecutive same-speaker segments with short pauses. Mirrors the first merge step of
    // the backend's `readable_transcription`; see getSegmentLineRanges for why that matters.
    const merged: Segment[] = [];
    for (const seg of raw) {
        const last = merged.length > 0 ? merged[merged.length - 1] : null;
        if (last && last.speaker === seg.speaker && (seg.pauseAfterPrev ?? 0) < SEGMENT_MERGE_MAX_SECONDS) {
            last.text += ' ' + seg.text;
            if (seg.duration !== undefined) {
                last.duration = (last.duration ?? 0) + (seg.pauseAfterPrev ?? 0) + seg.duration;
            }
        } else {
            merged.push({
                ...seg,
                speakerChanged: last ? last.speaker !== seg.speaker : false,
            });
        }
    }

    return merged;
}

// Maps each rendered segment to the 1-based line range it occupies in the backend's
// canonical WRAPPED numbering — `wrap_lines(readable_transcription(...))` — which is the
// same numbering event citations (`start_line`/`end_line`) are expressed in. Event citation
// clicks carry only those line numbers, so everything below MUST stay in sync with
// backend/src/utils/transcription_preprocessor.py and text_normalization.py.
//
// How the backend lays a transcription out (and what we replicate):
// 1. Same-speaker fragments with a pause < SEGMENT_MERGE_MAX_SECONDS merge silently — the same
//    rule as `deserializeSegments`, so segment[i] here is backend segment[i].
// 2. Consecutive same-speaker segments with a pause < INLINE_BREAK_MAX_SECONDS form one turn
//    (`groupIntoTurns`).
// 3. Each turn emits a header line (`reconstructHeaderLine`), then — when any segment has text —
//    one body line: the segment texts joined by ` [..N.Ns] ` pause markers (`pauseMarker`).
//    No blank separators.
// 4. Every logical line is WORD-WRAPPED at WRAP_WIDTH columns (`wrapLineStarts`).
// Rebuilding each body character for character lets us locate where every segment starts
// inside its turn's wrapped lines.

// Backend canonical wrap width (backend/src/utils/text_normalization.py WRAP_WIDTH).
// Changing it there must be mirrored here or citation scroll will drift.
const WRAP_WIDTH = 80;

// Code-point offsets at which each wrapped line of a single logical (newline-free) line starts.
// This MUST mirror `_wrap_logical_line` in backend/src/utils/text_normalization.py:
// greedy word-wrap, break on the whitespace run nearest to (and at/before) the column
// limit, hard-break an over-long token exactly at the limit, blank line => 1 line.
// Columns are counted in Unicode code points (Array.from) to match Python's str semantics.
function wrapLineStarts(cp: string[], width: number = WRAP_WIDTH): number[] {
    const n = cp.length;
    if (n === 0) return [0]; // blank line preserved as a single wrapped line

    const starts: number[] = [];
    let pos = 0;
    const isWs = (c: string) => c === ' ' || c === '\t';

    while (n - pos > width) {
        const limit = pos + width;
        starts.push(pos);

        // Search leftwards from `limit` for a whitespace char to break on.
        let breakIdx = -1;
        for (let i = limit; i > pos; i--) {
            if (isWs(cp[i])) { breakIdx = i; break; }
        }

        if (breakIdx === -1) {
            // Over-long token: hard-break at the column limit, consume nothing.
            pos = limit;
            continue;
        }

        // Expand the whitespace run around breakIdx; the whole run is consumed.
        let runStart = breakIdx;
        while (runStart - 1 >= pos && isWs(cp[runStart - 1])) runStart -= 1;
        let runEnd = breakIdx;
        while (runEnd + 1 < n && isWs(cp[runEnd + 1])) runEnd += 1;

        if (runStart === pos) {
            // Window is all leading whitespace: hard-break to guarantee progress.
            pos = limit;
        } else {
            pos = runEnd + 1;
        }
    }

    starts.push(pos); // trailing remainder (always <= width)
    return starts;
}

// Index of the wrapped line (from `wrapLineStarts`) that contains code-point `offset`.
function wrappedLineIndexOf(starts: number[], offset: number): number {
    let idx = 0;
    while (idx + 1 < starts.length && starts[idx + 1] <= offset) idx += 1;
    return idx;
}

// Inline same-speaker pause marker. Mirrors `pause_marker` in
// backend/src/utils/transcription_preprocessor.py (whose `_js_fixed` reproduces toFixed rounding),
// and must keep matching PAUSE_MARKER_RE in text_normalization.py.
const pauseMarker = (pause: number) => `[..${pause.toFixed(1)}s]`;

// Turn duration: segment durations plus the pauses between them. The backend header uses the
// same formula in the same summation order, so the formatted header text matches.
function turnDuration(segments: Segment[]): number {
    return segments.reduce(
        (sum, seg, j) => sum + (seg.duration ?? 0) + (j > 0 ? (seg.pauseAfterPrev ?? 0) : 0),
        0,
    );
}

// Reconstruct the backend turn header so its wrapped length matches. Headers are short so
// this is virtually always 1 wrapped line, but we count generally to stay correct.
// Mirrors readable_transcription: `### {speaker} | {turn duration}s[ [{pre_pause}s pause]]`.
function reconstructHeaderLine(turn: Segment[]): string {
    const first = turn[0];
    const prePause = first.pauseAfterPrev ?? 0;
    const pauseAnn = prePause >= 1.0 ? ` [${prePause.toFixed(2)}s pause]` : '';
    return `### ${first.speaker} | ${turnDuration(turn).toFixed(2)}s${pauseAnn}`;
}

type LineRange = { startLine: number; endLine: number };

function getSegmentLineRanges(segments: Segment[]): LineRange[] {
    const ranges: LineRange[] = new Array(segments.length);
    let line = 1;
    for (const turn of groupIntoTurns(segments)) {
        const headerStart = line;
        line += wrapLineStarts(Array.from(reconstructHeaderLine(turn.map(i => segments[i])))).length;

        // Rebuild the body the way the backend joins it, recording the code-point offset where
        // each texted segment begins (at its leading pause marker; the space before the marker
        // belongs to the previous segment).
        const body: string[] = [];
        const offsets: { index: number; offset: number }[] = [];
        for (const i of turn) {
            const text = (segments[i].text ?? '').trim();
            if (!text) continue;
            if (body.length > 0) body.push(' ');
            offsets.push({ index: i, offset: body.length });
            if (offsets.length > 1) body.push(...Array.from(pauseMarker(segments[i].pauseAfterPrev ?? 0) + ' '));
            body.push(...Array.from(text));
        }

        const bodyStart = line;
        const starts = body.length > 0 ? wrapLineStarts(body) : [];
        line += starts.length;

        offsets.forEach(({ index, offset }, k) => {
            const endOffset = k + 1 < offsets.length ? offsets[k + 1].offset - 1 : body.length - 1;
            ranges[index] = {
                startLine: bodyStart + wrappedLineIndexOf(starts, offset),
                endLine: bodyStart + wrappedLineIndexOf(starts, endOffset),
            };
        });

        // Segments without text (e.g. a live partial) have no body text; pin them to the line
        // of the nearest preceding texted segment, or the header.
        let prev: LineRange = { startLine: headerStart, endLine: headerStart };
        for (const i of turn) {
            if (ranges[i]) prev = ranges[i];
            else ranges[i] = { startLine: prev.endLine, endLine: prev.endLine };
        }
        // The turn's header lines belong to its first segment.
        ranges[turn[0]] = { ...ranges[turn[0]], startLine: headerStart };
    }
    return ranges;
}

// Given a 1-based citation line, find the index of the segment whose line span contains it.
// Several segments can share a wrapped line, so the start of a citation takes the first match
// and the end takes the last, keeping the highlighted range a superset of the cited text.
// Falls back to the last segment when the line sits past the end, so a slightly stale
// citation still lands somewhere sensible rather than failing silently.
function findSegmentIndexForLine(ranges: LineRange[], targetLine: number, pick: 'first' | 'last'): number | null {
    if (ranges.length === 0) return null;
    const contains = (r: LineRange) => targetLine >= r.startLine && targetLine <= r.endLine;
    if (pick === 'first') {
        for (let i = 0; i < ranges.length; i++) if (contains(ranges[i])) return i;
    } else {
        for (let i = ranges.length - 1; i >= 0; i--) if (contains(ranges[i])) return i;
    }
    // Past the last emitted line — clamp to the final segment.
    if (targetLine > ranges[ranges.length - 1].endLine) return ranges.length - 1;
    return null;
}

const formatSeconds = (duration: number | undefined) => duration ? duration.toFixed(2) + " s" : "?.?? s";

const AttrCol = observer(({ duration, speaker }: { duration: number | undefined, speaker: string | undefined }) => (
    <span className="transcript-attr">
        <span className="transcript-attr-speaker">{speaker === "Unknown" ? appState.t("transcription.unknownSpeaker") : speaker}</span>
        <span title={appState.t("transcription.phraseDuration")}>{formatSeconds(duration)}</span>
    </span>
));

const PauseMark = ({ duration }: { duration: number }) => (
    <span className="transcript-pause">… {formatSeconds(duration)}</span>
);

// Group consecutive same-speaker segments into turns; breaks shorter than
// INLINE_BREAK_MAX_SECONDS are rendered inline. Not display-only: the backend groups turns
// the same way in `readable_transcription`, and getSegmentLineRanges relies on this to
// compute event citation line numbers.
function groupIntoTurns(segments: Segment[]): number[][] {
    const turns: number[][] = [];
    segments.forEach((seg, i) => {
        const prev = i > 0 ? segments[i - 1] : null;
        if (prev && prev.speaker === seg.speaker && (seg.pauseAfterPrev ?? 0) < INLINE_BREAK_MAX_SECONDS) {
            turns[turns.length - 1].push(i);
        } else {
            turns.push([i]);
        }
    });
    return turns;
}

const SegmentText = ({ seg }: { seg: Segment }) => (
    <>
        {seg.text && <FadingSpan text={seg.text} speaker={seg.speaker} />}
        {seg.partial && (
            <span className="partial-text">
                {seg.text ? ' ' : ''}{seg.partial}
            </span>
        )}
    </>
);

type HighlightRange = { start: number; end: number } | null;

interface TurnRowProps {
    segments: Segment[];
    indices: number[];
    highlight: HighlightRange;
    segmentRefs: React.MutableRefObject<(HTMLElement | null)[]>;
}

// Memoized so a partial update only re-renders the last turn; segment objects keep identity unless changed.
const TurnRow = React.memo(({ segments, indices, highlight, segmentRefs }: TurnRowProps) => {
    const first = segments[0];
    const inRange = (i: number) => highlight != null && i >= highlight.start && i <= highlight.end;
    const turnActive = inRange(indices[0]);
    const duration = turnDuration(segments);

    return (
        <div
            // Register DOM nodes per segment so a citation jump can scroll to it.
            ref={el => { segmentRefs.current[indices[0]] = el; }}
            className={`transcript-phrase ${turnActive ? 'transcript-phrase-active' : ''}`}
        >
            {first.pauseAfterPrev !== undefined && first.pauseAfterPrev > 0 && (
                <PauseMark duration={first.pauseAfterPrev} />
            )}
            <AttrCol duration={duration} speaker={first.speaker} />
            <span>
                <SegmentText seg={first} />
                {segments.slice(1).map((seg, j) => {
                    const i = indices[j + 1];
                    return (
                        <span
                            key={i}
                            ref={el => { segmentRefs.current[i] = el; }}
                            className={!turnActive && inRange(i) ? 'transcript-phrase-active' : undefined}
                        >
                            {' '}<span className="transcript-pause">{pauseMarker(seg.pauseAfterPrev ?? 0)}</span>{' '}
                            <SegmentText seg={seg} />
                        </span>
                    );
                })}
            </span>
        </div>
    );
}, (a, b) =>
    a.highlight === b.highlight
    && a.indices[0] === b.indices[0]
    && a.segments.length === b.segments.length
    && a.segments.every((seg, j) => seg === b.segments[j])
);

const FadingSpan = ({ text, speaker }: { text: string, speaker: string }) => {
    const [opacity, setOpacity] = useState(0);

    useEffect(() => {
        // Trigger opacity transition after mount
        requestAnimationFrame(() => setOpacity(1));
    }, []);

    return (
        <span style={{ opacity: opacity, transition: 'opacity 0.5s ease-in-out' }} title={speaker}>
            {text}
        </span>
    );
}

export const RecordButton = observer(({
    isRecording,
    isStarting,
    onStart,
    onStop,
    micPermission,
    analyserRef,
    size = 40,
}: {
    isRecording: boolean,
    isStarting: boolean,
    onStart: () => void,
    onStop: () => void,
    micPermission: boolean | null,
    analyserRef: React.RefObject<AnalyserNode | null>,
    /** Outer diameter in pixels. Inner dot, disabled size, and border scale from this. */
    size?: number,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [audioLevel, setAudioLevel] = useState(0);
    const animationFrameRef = useRef<number | null>(null);

    // Original classes used this project's custom spacing (`w-4` = 4.5rem) while
    // `w-10`/`w-8` are not in the scale, so the container hug the inner and the
    // dot filled the button. Keep that ratio: inner == size; hover/recording
    // scales create the visual gap. Border was a fixed 3px on that ~72px button.
    const inner = size;
    const disabled = size;
    const border = Math.max(1, Math.round(size * 3 / 72));
    const containerStyle = { width: size, height: size } as const;
    const innerStyle = { width: inner, height: inner } as const;

    useEffect(() => {
        if (!isRecording) {
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
            setAudioLevel(0);
            return;
        }
        const update = () => {
            if (analyserRef.current) {
                const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
                analyserRef.current.getByteFrequencyData(dataArray);
                let sum = 0;
                for (let i = 0; i < dataArray.length; i++) sum += dataArray[i];
                setAudioLevel((sum / dataArray.length) / 2.55);
            }
            animationFrameRef.current = requestAnimationFrame(update);
        };
        animationFrameRef.current = requestAnimationFrame(update);
        return () => {
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        };
    }, [isRecording, analyserRef]);

    if (micPermission === false) {
        return (
            <div
                className="record-btn record-btn-disabled group"
                style={{ width: disabled, height: disabled }}
            >
                <div className="record-btn-dot-disabled" style={innerStyle}></div>
            </div>
        );
    }

    if (isRecording || isStarting) {
        return (
            <div
                className="record-btn group"
                style={containerStyle}
                onMouseEnter={() => !isStarting && setIsHovered(true)}
                onMouseLeave={() => {
                    setIsHovered(false);
                }}
                onClick={() => {
                    onStop();
                }}
                title={appState.t("transcription.stop")}
            >
                {/* Outer Ring: Permanent Red */}
                <div
                    className="record-btn-ring record-btn-ring-live"
                    style={{ borderWidth: border }}
                ></div>

                {/* Inner Shape */}
                {false ? (
                    // Stop Icon (Black Square)
                    <div
                        className="rounded-full bg-gray transition-all duration-200"
                        style={{
                            ...innerStyle,
                            transform: `scale(0.6)`, // Scale 20px base up
                        }}
                    ></div>
                ) : (
                    // Audio Level Indicator (Green Ring or Dot in center)
                    // We render a green circle that scales with audio level 
                    // Base size 0.5 (half container), scale adds up to 0.4
                    <div
                        className="record-btn-level"
                        style={{
                            ...innerStyle,
                            transform: `scale(${!isHovered ? (0.6 + (audioLevel / 1000)) : 0.6})`, // Scale 20px base up
                            opacity: !isHovered ? (0.1 + (audioLevel / 50)) : 1
                        }}
                    ></div>
                )}
            </div>
        );
    }

    return (
        <div
            className={`record-btn group ${isHovered ? 'record-btn-hover' : ''}`}
            style={containerStyle}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={() => {
                onStart();
            }}
            title={appState.t("transcription.start")}
        >
            {/* Outer Ring: Red, appears on hover with opacity 0.5 */}
            {/* Note: User requested "Recording... outer red ring should be the SAME size as when in idle hover". in Idle hover it is opacity 0.5. In Rec it is permanent. */}
            <div
                className="record-btn-ring record-btn-ring-idle"
                style={{ borderWidth: border }}
            ></div>

            {/* Inner Circle: Red, always visible */}
            <div
                className="record-btn-dot-idle"
                style={innerStyle}
            ></div>
        </div>
    );
});

export const TranscriptionComponent: React.FC<TranscriptionComponentProps> = observer(({ onTranscriptionUpdate, eventId, scrollToLine, scrollToEndLine, scrollNonce }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [isStarting, setIsStarting] = useState(false);

    // We store segments to animate them individually when they become final
    const [segments, setSegments] = useState<Segment[]>([]);
    const [transcript, setTranscript] = useState("");
    const [speakerList, setSpeakerList] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [micPermission, setMicPermission] = useState<boolean | null>(null);
    const [recordingDuration, setRecordingDuration] = useState<number>(0);
    const [sensivity, setSensivity] = useState<string>("0.05");
    const [loadingTranscription, setLoadingTranscription] = useState(false);
    const [hasSummary, setHasSummary] = useState(false);
    const [unsummarised, setUnsummarised] = useState(false);
    const [isSummarizing, setIsSummarizing] = useState(false);
    const transcriptionLocked = hasSummary || unsummarised || isSummarizing;

    // Hand this modal's close behaviour to the enclosing Modal so its single close affordances
    // (× / Escape / backdrop) drive it: confirm-before-close while there's an unsaved transcript,
    // immediate close once a summary already exists or summarisation is in progress.
    useModalClose((dismiss) => {
        if (transcriptionLocked) {
            dismiss();
            return;
        }
        appState.appendModal({
            title: appState.t("transcription.unsavedTitle"),
            body: appState.t("transcription.unsavedBody"),
            action: () => dismiss(),
        });
    });

    const clientRef = useRef<RealtimeClient | null>(null);
    const backendSocketRef = useRef<WebSocket | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const processorRef = useRef<AudioWorkletNode | null>(null);
    const inputRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const analyserRef = useRef<AnalyserNode | null>(null);
    const lastEndTimeRef = useRef<number | null>(null);
    const recordingStartTimeRef = useRef<number | null>(null);
    const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const intentRef = useRef(false);



    const wakeLockRef = useRef<WakeLockSentinel | null>(null);

    async function requestWakeLock() {
        try {
            wakeLockRef.current = await navigator.wakeLock.request('screen');
            wakeLockRef.current.addEventListener('release', () => {
                console.log('Wake lock released');
            });
            console.log('Wake lock acquired');
        } catch (err) {
            console.log(`Wake lock failed: ${err}`);
            postFrontendError(err);
        }
    }


    const checkMicPermission = useCallback(() => {
        setMicPermission(null);
        navigator.mediaDevices.getUserMedia({ audio: true })
            .then((stream) => {
                setMicPermission(true);
                setError(null);
                // We stop the tracks immediately, we just wanted to check permission/availability
                stream.getTracks().forEach(track => track.stop());
            })
            .catch((err) => {
                console.error("Mic check failed", err);
                setMicPermission(false);
            });
    }, []);

    // Initial mic check
    useEffect(() => {
        checkMicPermission();
    }, []);

    // Load existing transcription on mount
    useEffect(() => {
        if (!eventId) return;
        setLoadingTranscription(true);
        getTranscription({ body: { event_id: eventId } })
            .then((res: any) => {
                const data = res.data;
                if (data?.transcription) {
                    const loaded = deserializeSegments(data.transcription);
                    setSegments(loaded);
                    segmentsRef.current = loaded;
                    setSpeakerList(
                        [...new Set(loaded.map(s => s.speaker).filter(s => s !== 'Unknown'))].sort()
                    );
                    setTranscript(data.transcription);
                }
                setHasSummary(Boolean(data?.has_summary));
                setUnsummarised(Boolean(data?.unsummarised));
                setIsSummarizing(
                    Boolean(data?.is_summarizing) && !data?.has_summary && !data?.unsummarised
                );
            })
            .catch((err: any) => {
                console.error("Failed to load transcription", err);
            })
            .finally(() => setLoadingTranscription(false));
    }, [eventId]);

    // While summarisation holds the event lock, poll until the lock clears.
    useEffect(() => {
        if (!eventId || !isSummarizing) return;
        let cancelled = false;
        const poll = async () => {
            try {
                const res: any = await getTranscription({ body: { event_id: eventId } });
                if (cancelled || !res.data) return;
                if (res.data.has_summary) {
                    setHasSummary(true);
                    setIsSummarizing(false);
                    return;
                }
                if (res.data.unsummarised) {
                    setUnsummarised(true);
                    setIsSummarizing(false);
                    return;
                }
                if (!res.data.is_summarizing) {
                    setIsSummarizing(false);
                    appState.setModal({
                        title: appState.t("transcription.summaryErrorTitle"),
                        body: appState.t("transcription.summaryErrorBody"),
                    });
                }
            } catch (err) {
                console.error("Failed to poll transcription summary status", err);
            }
        };
        const timer = setInterval(poll, 2000);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [eventId, isSummarizing]);

    // Recording duration timer
    useEffect(() => {
        if (isRecording) {
            recordingStartTimeRef.current = Date.now();
            setRecordingDuration(0);
            durationIntervalRef.current = setInterval(() => {
                if (recordingStartTimeRef.current !== null) {
                    const duration = (Date.now() - recordingStartTimeRef.current) / 1000;
                    setRecordingDuration(duration);
                    // Auto-stop after 5 minutes of no new transcription (also releases wake lock)
                    if (lastEndTimeRef.current && ((duration - lastEndTimeRef.current) > 300)) {
                        stopRecording();
                        postFrontendError("Recording stopped due to inactivity");
                    }
                }
            }, 250);

            requestWakeLock();
        } else {
            if (durationIntervalRef.current) {
                clearInterval(durationIntervalRef.current);
                durationIntervalRef.current = null;
            }
            if (wakeLockRef.current) {
                wakeLockRef.current.release().then(() => {
                    wakeLockRef.current = null;
                });
            }
            recordingStartTimeRef.current = null;
        }
        return () => {
            if (durationIntervalRef.current) {
                clearInterval(durationIntervalRef.current);
                durationIntervalRef.current = null;
            }
            if (wakeLockRef.current) {
                wakeLockRef.current.release().then(() => {
                    wakeLockRef.current = null;
                });
            }
        };
    }, [isRecording]);


    const stopRecording = useCallback(() => {
        setIsRecording(false);
        setIsStarting(false);

        intentRef.current = false;

        // 1. Stop Audio Source immediately (so no new data is sent)
        if (processorRef.current) {
            processorRef.current.disconnect();
            // Don't null it yet if we want to be clean, but disconnecting stops data flow
        }
        if (inputRef.current) inputRef.current.disconnect();
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
        }
        // 2. Stop Recognition
        if (clientRef.current) {
            try {
                // This sends EndOfStream and waits for EndOfTranscript
                // Note: client.stopRecognition() is async
                clientRef.current.stopRecognition().catch(err => console.warn("Stop error:", err));
            } catch (err) {
                console.warn("Error stopping recognition:", err);
            }
        }
    }, []);

    // Actual cleanup function to be called when we are truly done (EndOfTranscript or Unmount)
    const cleanup = useCallback(() => {
        if (backendSocketRef.current) {
            if (backendSocketRef.current.readyState === WebSocket.OPEN) {
                backendSocketRef.current.send(JSON.stringify({ action: "end" }));
            }
            backendSocketRef.current.close();
            backendSocketRef.current = null;
        }
        if (clientRef.current) {
            clientRef.current = null;
        }
        if (processorRef.current) {
            processorRef.current.disconnect();
            processorRef.current = null;
        }
        if (inputRef.current) {
            inputRef.current.disconnect();
            inputRef.current = null;
        }
        if (analyserRef.current) {
            analyserRef.current.disconnect();
            analyserRef.current = null;
        }
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
        }
    }, []);

    const segmentsRef = useRef<Segment[]>([]);

    useEffect(() => {
        segmentsRef.current = segments;
    }, [segments]);

    // DOM refs to each rendered segment row, keyed by segment index, so we can scroll a
    // specific segment into view when arriving from a citation click.
    const segmentRefs = useRef<(HTMLElement | null)[]>([]);
    const turns = useMemo(() => groupIntoTurns(segments), [segments]);
    // Inclusive segment-index range to briefly highlight after a citation jump (null = none).
    const [highlightedRange, setHighlightedRange] = useState<{ start: number; end: number } | null>(null);

    // When a `scrollToLine` is provided (event-citation jump), translate the cited line range
    // into segment indices, scroll the first into view, and highlight every segment the citation
    // spans. Runs whenever the segments load or the target changes — segments arrive
    // asynchronously, so this re-fires once the fetch populates them. `scrollNonce` lets the
    // same line range re-flash when the user clicks the same citation again.
    useEffect(() => {
        if (scrollToLine == null || segments.length === 0) return;

        const ranges = getSegmentLineRanges(segments);
        const startIdx = findSegmentIndexForLine(ranges, scrollToLine, 'first');
        if (startIdx == null) return;
        // End line maps to its own segment; fall back to the start segment when absent or when
        // it resolves before the start (defensive).
        const endLine = scrollToEndLine ?? scrollToLine;
        const endIdx = findSegmentIndexForLine(ranges, endLine, 'last') ?? startIdx;
        const lo = Math.min(startIdx, endIdx);
        const hi = Math.max(startIdx, endIdx);

        const el = segmentRefs.current[lo];
        if (!el) return;

        scrollIntoContainer(el, { behavior: 'smooth', block: 'center' });
        setHighlightedRange({ start: lo, end: hi });
        // Clear the highlight after a short flash so it reads as a transient cue, not a
        // persistent selection.
        const timer = setTimeout(() => setHighlightedRange(null), 2500);
        return () => clearTimeout(timer);
    }, [segments, scrollToLine, scrollToEndLine, scrollNonce]);

    const [loadingSave, setLoadingSave] = useState(false);

    const generateSummaryReq = Request<GenerateEventTranscriptionSummaryData, any>(
        generateEventTranscriptionSummary,
        () => ({
            body: {
                event_id: eventId!,
                conversation_id: chatState.conversationId ?? undefined,
                user_instructions: userInstructions.current,
            },
        }),
        setLoadingSave,
        (_data: any) => {
            // complete=true: short save set transcription_unsummarised
            // (no LLM summary). Lock the OEW; do not claim hasSummary.
            if (_data?.complete) {
                setUnsummarised(true);
                setIsSummarizing(false);
                return;
            }
            setIsSummarizing(true);
        },
        (_error: any) => {
            const detail = _error?.detail ?? _error?.data?.detail;
            if (detail === "event_summarisation_in_progress") {
                setIsSummarizing(true);
                return;
            }
            setHasSummary(false);
            setUnsummarised(false);
            setIsSummarizing(false);
            appState.setModal({ title: appState.t("transcription.summaryErrorTitle"), body: appState.t("transcription.summaryErrorBody") });
        },
        () => eventId !== undefined && eventId > 0,
    );

    const [userInstructions, setUserInstructions] = useRefState("");

    const handleSaveClick = useCallback(() => {
        appState.appendModal({
            title: appState.t("transcription.saveTitle"),
            body: <div className="h-full w-full flex flex-col gap-1 p-2">
                <p className="b w-full">
                    {appState.t("transcription.saveBody")}
                </p>
                <textarea className="textarea h-5"
                    defaultValue={userInstructions.current}
                    onChange={(e) => setUserInstructions(e.target.value)}
                />
            </div>,
            action: () => {
                // Show processing immediately so Save/Clear/Record disappear; revert if the request fails.
                setIsSummarizing(true);
                generateSummaryReq();
            },
        });
    }, [generateSummaryReq]);


    const handleClear = useCallback(async () => {
        if (!eventId) return;
        appState.appendModal({
            title: appState.t("transcription.clearTitle"),
            body: appState.t("transcription.clearBody"),
            action: async () => {
                appState.popModal();
                try {
                    const res: any = await clearTranscription({ body: { event_id: eventId } });
                    if (res.data) {
                        setSegments([]);
                        setSpeakerList([]);
                        segmentsRef.current = [];
                        setTranscript("");
                        lastEndTimeRef.current = null;
                        setHasSummary(false);
                        setUnsummarised(false);
                        setIsSummarizing(false);
                    }
                } catch (err: any) {
                    setError(err.message || appState.t("transcription.clearFailed"));
                }
            }
        })
    }, [eventId]);

    const startRecording = useCallback(async () => {
        setError(null);
        intentRef.current = true;

        let temporaryStream: MediaStream | null = null;
        try {
            setIsStarting(true);

            temporaryStream = await navigator.mediaDevices.getUserMedia({ audio: true });

            if (!intentRef.current) return temporaryStream.getTracks().forEach(t => t.stop());

            const deviceLabel = temporaryStream.getAudioTracks()[0]?.label || appState.t("transcription.defaultMic");

            temporaryStream.getTracks().forEach(t => t.stop());

            // Figure out who needs profiles
            const missingEntities: { type: 'user' | 'tag', id?: number, name: string, profile: any }[] = [];

            if (appState.user && !hasProfileForDevice(appState.user.speaker_profile, deviceLabel)) {
                missingEntities.push({ type: 'user', name: appState.user.full_name, profile: appState.user.speaker_profile });
            }

            const eventTagId = eventId ? (chatState.chatObjects.get({ type: "event", id: eventId } as any) as any)?.event?.tag_id : undefined;
            const eventTag = eventTagId ? (appState.tags ?? []).find((t: any) => t.id === eventTagId) : undefined;
            const isClientEvent = eventTag?.attributes?.is_client === true;

            const participatingTags = (appState.tags ?? []).filter((tag: any) => {
                if (isClientEvent && eventTag && tag.id === eventTag.id) return true;
                if (!isClientEvent && tag.attributes?.is_client && chatState.chatObjects.get({ type: "tag", id: tag.id } as any)) return true;
                return false;
            });

            for (const tag of participatingTags) {
                if (!hasProfileForDevice(tag.attributes?.speaker_profile, deviceLabel)) {
                    missingEntities.push({ type: 'tag', id: tag.id, name: tag.name, profile: tag.attributes?.speaker_profile });
                }
            }


            if (missingEntities.length === 0) {
                // Everyone has a profile for this device
                performStartRecording(deviceLabel);
                return;
            }

            setIsStarting(false);

            const processNext = (index: number) => {
                if (index >= missingEntities.length) {
                    // All profiles captured — don't auto-start recording.
                    // User must click the record button again, which ensures
                    // async profile saves have completed before we read them.
                    return;
                }
                const entity = missingEntities[index];
                appState.appendModal({
                    title: appState.t("transcription.transcribe"),
                    body: <div className='p-2'>
                        <SpeakerProfileRecorder
                            existingProfile={entity.profile}
                            isPopup={true}
                            label={entity.name}
                            deviceLabel={deviceLabel}
                            onProfileCaptured={async (profileArr) => {
                                if (entity.type === 'user') {
                                    const updatedProfile = appendProfile(appState.user?.speaker_profile, deviceLabel, profileArr);
                                    appState.setSpeakerProfile({ speaker_profile: updatedProfile });
                                } else if (entity.id) {
                                    const currentTagProfile = (appState.tags.find(t => t.id === entity.id) as any)?.attributes?.speaker_profile;
                                    const updatedProfile = appendProfile(currentTagProfile, deviceLabel, profileArr);
                                    await updateTagSpeakerProfile({ body: { id: entity.id, speaker_profile: updatedProfile } });
                                    await appState.getTags({ pageNumber: 0, search: "" });
                                }
                                appState.popModal();
                                processNext(index + 1);
                            }}
                        />
                    </div>,
                    close: () => {
                        // User aborted
                        appState.popModal();
                        setError(appState.t("transcription.recordingAborted"));
                    }
                });
            };

            if (!intentRef.current) return;
            processNext(0);

        } catch (err: any) {
            console.error("Failed to check microphone before starting", err);
            setError(appState.t("transcription.micDenied"));
            if (temporaryStream) temporaryStream.getTracks().forEach(t => t.stop());
        }
    }, [eventId, appState.user, appState.tags, chatState.chatObjects]);

    const performStartRecording = useCallback(async (selectedDeviceLabel?: string) => {
        setError(null);
        lastEndTimeRef.current = null;
        intentRef.current = true;

        try {
            // 1. Get Token
            const tokenRes = await fetch('/api/transcription/get_atot_token');
            if (!tokenRes.ok) {
                throw new Error(`Failed to get token: ${tokenRes.statusText}`);
            }
            const { token } = await tokenRes.json();

            if (!intentRef.current) return;

            if (!token) {
                throw new Error("No token received from backend");
            }

            // 2. Setup Audio
            // We will set the sample rate at the source if supported by the browser
            // Most browsers ignore this constraints and use the system default (44.1/48k)

            // Create AudioContext with explicit sample rate if possible
            // Note: If the hardware doesn't support it, the browser might resample automatically 
            // or fail. To be safe, let's try to get 16k, but fallback to default.
            const audioContext = new AudioContext({ latencyHint: 'balanced' });
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    sampleRate: audioContext.sampleRate, // Request the AudioContext's sample rate (may be ignored by browser)
                    channelCount: 1,
                    // Call-oriented noise suppression strips quiet/distant speakers and hurts ASR + diarization.
                    noiseSuppression: false
                }
            });

            if (!intentRef.current) {
                audioContext.close();
                return stream.getTracks().forEach(t => t.stop());
            }

            // Created outside the click handler (after awaits), so autoplay policy may start it suspended.
            if (audioContext.state === 'suspended') {
                audioContext.resume().catch(err => postFrontendError(err));
            }

            mediaStreamRef.current = stream;
            audioContextRef.current = audioContext;

            const source = audioContext.createMediaStreamSource(stream);
            inputRef.current = source;

            // Analyser for visualization
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 64;
            source.connect(analyser);
            analyserRef.current = analyser;

            // 3. Setup backend transcription WebSocket (streams text to event transcription)
            if (eventId) {
                initializeBackendWebSocket();
            }

            // 4. Setup Speechmatics Client (using token as JWT)
            // Note: We use the URL from the constant to ensure we hit the same region

            // Resolve the event's associated tag once; used for session-type detection
            const eventTagId = eventId
                ? (chatState.chatObjects.get({ type: "event", id: eventId } as any) as any)?.event?.tag_id
                : undefined;
            const eventTag = eventTagId
                ? (appState.tags ?? []).find((t: any) => t.id === eventTagId)
                : undefined;
            // 1-on-1 session: the event belongs directly to a client tag
            const isClientEvent = eventTag?.attributes?.is_client === true;
            const userLabel = appState.user?.full_name || "User";

            const client = new RealtimeClient({ url: SM_URL });
            clientRef.current = client;

            // Applies one finalized same-speaker run to the segment list and returns its serialized line.
            const appendFinalRun = ({ speaker, text: newTranscript, startTime, endTime }: SpeakerRun): string => {
                const duration = endTime - startTime;

                // Calculate Pause (gap before this phrase)
                let pauseLength = 0;
                const isFirstSegmentOfSession = lastEndTimeRef.current === null;
                if (lastEndTimeRef.current !== null) {
                    pauseLength = startTime - lastEndTimeRef.current;
                    if (pauseLength < 0) pauseLength = 0;
                }

                // Update global last end time for the NEXT segment
                lastEndTimeRef.current = endTime;

                // We rely on setSegments functional update to determine speaker change correctly
                setSegments(prev => {
                    // First, prepare base array: clear partial from last segment or strip pure-open segment
                    let base = [...prev];
                    if (base.length > 0 && base[base.length - 1].partial !== undefined) {
                        const last = base[base.length - 1];
                        if (last.text) {
                            // Finalized segment with appended partial — just clear partial
                            base[base.length - 1] = { ...last, partial: undefined };
                        } else {
                            // Pure open segment (no finalized text) — remove it
                            base = base.slice(0, -1);
                        }
                    }

                    const lastSeg = base.length > 0 ? base[base.length - 1] : null;
                    const speakerChanged = lastSeg ? (lastSeg.speaker !== speaker) : false;

                    // Merge if same speaker and pause is short, but not first segment of new session.
                    // Must match the merge in deserializeSegments so live segments line up with the
                    // backend's citation line numbers.
                    if (lastSeg && !speakerChanged && pauseLength < SEGMENT_MERGE_MAX_SECONDS && !isFirstSegmentOfSession) {
                        const updatedLast: Segment = {
                            ...lastSeg,
                            text: lastSeg.text + " " + newTranscript,
                            endTime: endTime,
                            duration: (endTime !== undefined && lastSeg.startTime !== undefined)
                                ? (endTime - lastSeg.startTime)
                                : ((lastSeg.duration || 0) + duration),
                        };
                        base[base.length - 1] = updatedLast;
                        segmentsRef.current = base;
                        return base;
                    }

                    const newSegment: Segment = {
                        speaker,
                        text: newTranscript,
                        startTime,
                        endTime,
                        duration,
                        pauseAfterPrev: pauseLength,
                        speakerChanged
                    };

                    const updated = [...base, newSegment];
                    segmentsRef.current = updated;
                    return updated;
                });

                // Update speaker list
                if (speaker && speaker !== "Unknown") {
                    setSpeakerList(prev => {
                        if (!prev.includes(speaker)) {
                            return [...prev, speaker].sort();
                        }
                        return prev;
                    });
                }

                return `\n[${speaker}|${pauseLength.toFixed(2)}s|${duration.toFixed(2)}s] ${newTranscript}`;
            };

            client.addEventListener('receiveMessage', ({ data }) => {
                const message = data;

                if (message.message === 'AddTranscript') {
                    if (message.metadata && message.metadata.transcript) {
                        let runs = Array.isArray(message.results) ? splitIntoSpeakerRuns(message.results) : [];
                        if (runs.length === 0) {
                            runs = [{
                                speaker: (message.metadata as any).speaker || "Unknown",
                                text: message.metadata.transcript.trim(),
                                startTime: message.metadata.start_time,
                                endTime: message.metadata.end_time,
                            }];
                        }

                        // One backend send per message: a reconnect replaces the pending text, so per-run sends could drop lines.
                        const serializedLines = runs.map(appendFinalRun).join("");

                        if (backendSocketRef.current && backendSocketRef.current.readyState === WebSocket.OPEN) {
                            backendSocketRef.current.send(JSON.stringify({ text: serializedLines }));
                        } else {
                            postFrontendError("WebSocket not connected when sending transcript, trying to reconnect again")

                            initializeBackendWebSocket(serializedLines);
                        }
                    }
                } else if (message.message === 'AddPartialTranscript') {
                    if (message.metadata && message.metadata.transcript) {
                        const partialText = message.metadata.transcript;
                        const firstPartialWord = message.results?.find(r => r.type !== 'punctuation');
                        const partialStartTime = firstPartialWord?.start_time ?? message.metadata.start_time;

                        // Extract speaker from partial
                        const partialSpeaker = firstPartialWord?.alternatives?.[0]?.speaker || "Unknown";

                        // Update speaker list if needed
                        if (partialSpeaker && partialSpeaker !== "Unknown") {
                            setSpeakerList(prev => {
                                if (!prev.includes(partialSpeaker)) return [...prev, partialSpeaker].sort();
                                return prev;
                            });
                        }

                        // Calculate pause from last known end time
                        let partialPause = 0;
                        if (lastEndTimeRef.current !== null && partialStartTime !== undefined) {
                            partialPause = partialStartTime - lastEndTimeRef.current;
                            if (partialPause < 0) partialPause = 0;
                        }

                        // Check if speaker changed (only when we have a known partial speaker)
                        setSegments(prev => {
                            if (prev.length === 0) {
                                // No segments at all — create new open segment
                                const newSeg: Segment = {
                                    speaker: partialSpeaker,
                                    text: "",
                                    partial: partialText,
                                    startTime: partialStartTime
                                };
                                const updated = [newSeg];
                                segmentsRef.current = updated;
                                return updated;
                            }

                            const last = prev[prev.length - 1];
                            const lastSpeaker = last.speaker;

                            // If the last segment is a pure-open segment (no finalized text),
                            // just update its partial — the split was already done
                            if (!last.text && last.partial !== undefined) {
                                const updated = [...prev];
                                updated[prev.length - 1] = {
                                    ...last,
                                    partial: partialText,
                                    speaker: (lastSpeaker === "Unknown" && partialSpeaker !== "Unknown")
                                        ? partialSpeaker
                                        : lastSpeaker
                                };
                                segmentsRef.current = updated;
                                return updated;
                            }

                            const speakerChanged = partialSpeaker !== "Unknown"
                                && lastSpeaker !== "Unknown"
                                && partialSpeaker !== lastSpeaker;

                            // If long pause or speaker change, start a new segment. Same threshold as
                            // deserializeSegments so live segments line up with the backend's.
                            if (speakerChanged || partialPause >= SEGMENT_MERGE_MAX_SECONDS) {
                                // Clear partial from previous segment before creating new one
                                const updated = [...prev];
                                if (last.partial !== undefined) {
                                    updated[prev.length - 1] = { ...last, partial: undefined };
                                }
                                const newSeg: Segment = {
                                    speaker: partialSpeaker !== "Unknown" ? partialSpeaker : "Unknown",
                                    text: "",
                                    partial: partialText,
                                    startTime: partialStartTime,
                                    pauseAfterPrev: partialPause,
                                    speakerChanged
                                };
                                updated.push(newSeg);
                                segmentsRef.current = updated;
                                return updated;
                            }

                            // Same speaker, short pause — attach partial to last segment
                            const updated = [...prev];
                            updated[prev.length - 1] = {
                                ...last,
                                partial: partialText,
                                // Only upgrade speaker from Unknown, never overwrite a known one
                                speaker: (lastSpeaker === "Unknown" && partialSpeaker !== "Unknown")
                                    ? partialSpeaker
                                    : lastSpeaker
                            };
                            segmentsRef.current = updated;
                            return updated;
                        });
                    }
                } else if (message.message === 'SpeakersResult') {
                    // SpeakersResult is not used anymore as we pre-record all profiles
                } else if (message.message === 'EndOfTranscript') {
                    cleanup();
                    // Finalize state
                    setIsRecording(false);

                    // We keep 'segments' for display instead of clearing them.
                    // We also update 'transcript' string for copy/paste if needed.
                    setTranscript(serializeSegments(segmentsRef.current));

                } else if (message.message === 'Error') {
                    intentRef.current = false;
                    console.error("Speechmatics Error:", message);
                    setError(`Error: ${message.reason}`);
                    postFrontendError({
                        error: message, setup: {
                            audio_format: {
                                type: 'raw',
                                encoding: 'pcm_f32le',
                                sample_rate: audioContext.sampleRate // We enforce this in AudioContext

                            },
                            transcription_config: {
                                language: "da",
                                diarization: "speaker",
                                enable_partials: true,
                                operating_point: 'enhanced',
                                speaker_diarization_config
                            }
                        }, device: selectedDeviceLabel, client: clientRef.current?.socketState ?? "no running client"
                    });
                    cleanup();

                    setIsRecording(false);
                }
            });

            const predefinedSpeakers: any[] = [];

            if (appState.user?.speaker_profile?.length) {
                predefinedSpeakers.push({
                    label: userLabel.trimEnd(),
                    speaker_identifiers: getAllIdentifiers(appState.user.speaker_profile)
                });
            }

            const participatingTags = (appState.tags ?? []).filter((tag: any) => {
                if (isClientEvent && eventTag && tag.id === eventTag.id) return true;
                if (!isClientEvent && tag.attributes?.is_client && chatState.chatObjects.get({ type: "tag", id: tag.id } as any)) return true;
                return false;
            });

            for (const tag of participatingTags) {
                if (tag.attributes?.speaker_profile?.length) {
                    predefinedSpeakers.push({
                        label: tag.name.trimEnd(),
                        speaker_identifiers: getAllIdentifiers(tag.attributes.speaker_profile)
                    });
                }
            }

            const speaker_diarization_config: any = {
                speaker_sensitivity: parseFloat(sensivity),
                get_speakers: true
            };

            if (predefinedSpeakers.length > 0) {
                speaker_diarization_config.speakers = predefinedSpeakers;
            }

            if (!intentRef.current) return client.dispose();

            await client.start(token, {
                audio_format: {
                    type: 'raw',
                    encoding: 'pcm_f32le',
                    sample_rate: audioContext.sampleRate // We enforce this in AudioContext

                },
                transcription_config: {
                    language: "da",
                    diarization: "speaker",
                    enable_partials: true,
                    operating_point: 'enhanced',
                    speaker_diarization_config
                }
            });

            if (!intentRef.current) return client.stopRecognition();

            // The client has no reconnect; if Speechmatics drops mid-session, stop instead of silently discarding audio.
            client.addEventListener('socketStateChange', ({ socketState }) => {
                if (socketState !== 'closed' || !intentRef.current || clientRef.current !== client) return;
                intentRef.current = false;
                postFrontendError({ error: "Speechmatics socket closed during recording", device: selectedDeviceLabel });
                setError(appState.t("transcription.connectionLost"));
                setTranscript(serializeSegments(segmentsRef.current));
                cleanup();
                setIsRecording(false);
            });

            setIsRecording(true);
            // Worklet Message Handling
            // workletNode.port.onmessage = (event: MessageEvent) => {
            //     if (clientRef.current) {
            //         const inputData: Float32Array = event.data;
            //         clientRef.current.sendAudio(inputData);
            //     }
            // };
            // Send audio chunks directly to the Speechmatics client

            // AudioWorklet runs on a dedicated audio thread (not the main thread)
            // Batches 128-frame render quanta into ~85 ms chunks (at 48 kHz) to keep main-thread message/send rate low.
            const workletCode = `
                const CHUNK = 4096;
                class PCMProcessor extends AudioWorkletProcessor {
                    constructor() {
                        super();
                        this.buf = new Float32Array(CHUNK);
                        this.len = 0;
                    }
                    process(inputs) {
                        const ch = inputs[0] && inputs[0][0];
                        if (ch && ch.length > 0) {
                            let off = 0;
                            while (off < ch.length) {
                                const n = Math.min(ch.length - off, CHUNK - this.len);
                                this.buf.set(ch.subarray(off, off + n), this.len);
                                this.len += n;
                                off += n;
                                if (this.len === CHUNK) {
                                    this.port.postMessage(this.buf, [this.buf.buffer]);
                                    this.buf = new Float32Array(CHUNK);
                                    this.len = 0;
                                }
                            }
                        }
                        return true;
                    }
                }
                registerProcessor('pcm-processor', PCMProcessor);
            `;
            const blob = new Blob([workletCode], { type: 'application/javascript' });
            const workletUrl = URL.createObjectURL(blob);
            await audioContext.audioWorklet.addModule(workletUrl);
            URL.revokeObjectURL(workletUrl);

            const workletNode = new AudioWorkletNode(audioContext, 'pcm-processor');
            processorRef.current = workletNode;

            workletNode.port.onmessage = (event: MessageEvent) => {
                const activeClient = clientRef.current;
                // sendAudio throws on a non-open socket; chunks still queued after stop/close are dropped here.
                if (activeClient && activeClient.socketState === 'open') {
                    activeClient.sendAudio(event.data as Float32Array);
                }
            };

            source.connect(workletNode);
            workletNode.connect(audioContext.destination);


        } catch (err: any) {
            console.error("Recording setup error:", err);
            setError(err.message || "Failed to start recording");
            cleanup();
            setIsRecording(false);
        }


    }, [cleanup, eventId]); // Only depend on cleanup and eventId

    function initializeBackendWebSocket(initMsg?: string) {
        if (backendSocketRef.current) {
            try {
                backendSocketRef.current.close();
            } catch {
                // ignore
            }
            backendSocketRef.current = null;
        }

        // Match chat websockets: ws:// on http, wss:// on https. Hardcoded wss://
        // resets on local http (Firefox NS_ERROR_NET_RESET) and drops all chunks.
        const wsUrl = window.location.origin.replace(/^http/, "ws") + "/api/transcription/ws/transcribe";
        const backendWs = new WebSocket(wsUrl);
        backendSocketRef.current = backendWs;

        let pendingText = initMsg;

        backendWs.onopen = () => {
            if (backendWs.readyState === WebSocket.OPEN) {
                backendWs.send(JSON.stringify({ event_id: eventId }));
            } else {
                postFrontendError("WebSocket not open after connection established");
            }
        };

        backendWs.onmessage = (event) => {
            const msg = JSON.parse(event.data);
            if (msg.status === "error") {
                console.error("Backend transcription WS error:", msg.detail);
                setError(`Backend error: ${msg.detail}`);
                return;
            }
            // Backend only accepts text chunks after the init handshake ({ status: "ready" }).
            if (msg.status === "ready" && pendingText) {
                backendWs.send(JSON.stringify({ text: pendingText }));
                pendingText = undefined;
            }
        };

        backendWs.onerror = (e) => {
            console.error("Backend transcription WS error", e);
            postFrontendError(e)
        };

        backendWs.onclose = (e) => {
            if (backendSocketRef.current === backendWs) {
                backendSocketRef.current = null;
            }
            if (e.code !== 1000 && e.code !== 1005) {
                console.error("Backend transcription WS closed", e.code, e.reason);
            }
        };
    }

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            // Force cleanup on unmount
            cleanup();
        };
    }, [cleanup]);

    return (
        <div className="relative flex flex-col gap-[.5rem] w-full pt-1 pb-1">
            <div className="transcript-header">
            <label className="field-label">
                {appState.t("objects.transcriptionTitle")}
            </label>

            {isSummarizing && (
                <div className="status-strip">
                    <small className="hint">{appState.t("transcription.summarising")}</small>
                </div>
            )}

            {!transcriptionLocked && <div className="status-strip">

                {/* Record Button */}
                <RecordButton
                    isRecording={isRecording}
                    isStarting={isStarting}
                    onStart={startRecording}
                    onStop={stopRecording}
                    micPermission={micPermission}
                    analyserRef={analyserRef}
                    size={32}
                />

                {/* Duration Display */}
                <div className="status-strip-value w-4 mx-1 flex flex-col items-center">
                    {`${Math.floor(recordingDuration / 3600).toString().padStart(2, '0')}:${Math.floor((recordingDuration % 3600) / 60).toString().padStart(2, '0')}:${Math.floor(recordingDuration % 60).toString().padStart(2, '0')}`}
                </div>
                
                {/* Mic Status Text */}
                <div className="status-strip-value mx-1 flex flex-col items-center">
                    {isRecording ? appState.t("transcription.recording") : (micPermission === false ? appState.t("speaker.noMic") : "Ready")}
                </div>

                {micPermission === false
                    ? <></>
                    : error && <span className="error-text">{error}</span>
                }

                

                



            </div>
            }

            {/* {!hasSummary && <input aria-label='speaker sensivity' type='text' value={sensivity} onChange={x => setSensivity(x.target.value)} />} */}

            {/* Two Pane Layout */}
            {/* Sidebar - Speakers */}
            <div className={`flex flex-row items-center w-full h-fit`}>
                <div className="flex flex-row flex-wrap items-center min-w-0 w-full gap-[.35rem] mr-2">
                    {speakerList.map((speaker) => (
                        <span key={speaker} className="pill" title={appState.t("transcription.tagSpeaker")}>
                            {speaker}
                        </span>
                    ))}
                </div>
                {/* {speakerList.length === 0 && !hasSummary && <small>{appState.t("transcription.waitingAudio")}</small>} */}
                <div className="entry-actions justify-end shrink-0 gap-[.25rem]">
                    {eventId && !isRecording && segments.length > 0 && !transcriptionLocked && (
                        <ActionHandler className="btn-ghost" title={appState.t("transcription.clear")} keysTrigger="" funq={handleClear}>
                            <Remove />
                        </ActionHandler>
                    )}

                    {eventId && !isRecording && segments.length > 0 && !transcriptionLocked && (
                        <ActionHandler glow title={appState.t("transcription.saveAndSummarise")} keysTrigger="" funq={handleSaveClick} disabled={loadingSave}>
                            <Save />
                        </ActionHandler>
                    )}
                </div>
            </div>
            </div>

            {/* Main Pane - Transcription */}
            <div className="transcript-body">
                {loadingTranscription ? (
                    <p className="empty-state">{appState.t("transcription.loading")}</p>
                ) : segments.length > 0 || isRecording ? (
                    <div className="flex flex-col gap-[.25rem] w-full">
                        {turns.map(indices => (
                            <TurnRow
                                key={indices[0]}
                                segments={indices.map(i => segments[i])}
                                indices={indices}
                                highlight={highlightedRange}
                                segmentRefs={segmentRefs}
                            />
                        ))}
                    </div>
                ) : (
                    <p className="empty-state">{appState.t("transcription.empty")}</p>
                )}

            </div>

            {/* <div className="flex justify-end mt-2">
                <ActionHandler
                    title="Copy to Clipboard"
                    keysTrigger=""
                    funq={() => {
                        let currentText = "";
                        if (segments.length > 0 || isRecording) {
                            currentText = segments
                                .filter(s => s.text || s.partial)
                                .map(s => {
                                    const text = [s.text, s.partial].filter(Boolean).join(' ');
                                    return s.speaker !== 'Unknown' ? `[${s.speaker}] ${text}` : text;
                                }).join(" ");
                        } else {
                            currentText = transcript;
                        }

                        if (onTranscriptionUpdate) onTranscriptionUpdate(currentText);
                        navigator.clipboard.writeText(currentText);
                    }}
                >
                    <span className="px-2">Copy to Clipboard</span>
                </ActionHandler>
            </div> */}
        </div>
    );
});
